import { graphql, useLazyLoadQuery } from "react-relay";
import { generatePath } from "react-router";

import { paths } from "app/constants/paths";
import { useRouteParams } from "app/hooks/routing/useRouteParams";

import { NotificationsSettingsPage } from "../../settings/components/NotificationsSettingsPage";
import { UpsertNotificationRuleStepsContainer } from "../components/UpsertNotificationRuleStepsContainer";
import { NotificationRuleFormContextProvider } from "../contexts/NotificationRuleFormContext";
import { useNotificationRuleHelpContent } from "../hooks/useNotificationRuleHelpContent";
import { EditNotificationRuleHeader } from "./EditNotificationRuleHeader";

import type { EditNotificationRuleRootQuery } from "./__generated__/EditNotificationRuleRootQuery.graphql";

export function EditNotificationRuleRoot() {
  const { ruleUUID } = useRouteParams(paths.notificationRuleEdit);
  const data = useLazyLoadQuery<EditNotificationRuleRootQuery>(
    graphql`
      query EditNotificationRuleRootQuery($uuid: UUID!) {
        notificationRule(uuid: $uuid) {
          name
          ...UpsertNotificationRuleStepsContainer_notificationRule
          uuid
        }
        ...NotificationsSettingsPage_query
      }
    `,
    { uuid: ruleUUID ?? "" },
  );
  const helpContent = useNotificationRuleHelpContent();

  if (!data.notificationRule?.uuid) {
    return (
      <div className="text-body-small text-text-secondary">
        No notification rule found with this uuid.
      </div>
    );
  }

  return (
    <NotificationsSettingsPage
      additionalCrumbs={[
        // The rule's details page is the trail's parent level.
        {
          label: data.notificationRule.name,
          target: generatePath(paths.notificationRuleDetails, {
            ruleUUID: data.notificationRule.uuid,
          }),
        },
      ]}
      helpContent={helpContent}
      queryRef={data}
      title="Edit Notification Rule"
      withHeader={false}
    >
      <NotificationRuleFormContextProvider
        ruleUUID={data.notificationRule.uuid}
        isEditMode
      >
        <EditNotificationRuleHeader name={data.notificationRule.name} />
        <UpsertNotificationRuleStepsContainer
          notificationRule={data.notificationRule}
        />
      </NotificationRuleFormContextProvider>
    </NotificationsSettingsPage>
  );
}
